import React from "react";
import { Box, Typography, Avatar, Divider, Stack, styled } from "@mui/material";
import { FormatFullName } from "app/utils/helper";
import { useSelector } from "react-redux";
import { selectSelectedSchool } from "app/modules/schools/selectors";

// Template 1 (Original Design)
const TransportCardTemplate1 = ({
  student,
  index,
  pageIndex,
  color,
  selectedSession,
}) => {
  const selectedSchool = useSelector(selectSelectedSchool);
  const fullName = FormatFullName(
    student?.studentDocId?.firstName,
    student?.studentDocId?.middleName,
    student?.studentDocId?.lastName
  );

  return (
    <React.Fragment key={student?._id || `${pageIndex}-${index}`}>
      <CardContainer color={color}>
        {/* Top header with school name, transport card title and address */}
        {(() => {
          return (
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Box direction="row" alignItems="center" display="flex" gap={1}>
                <Avatar
                  variant="square"
                  src={selectedSchool?.schoolLogo || "image-placeholder.jpeg"}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 1.5,
                    objectFit: "cover",
                  }}
                />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    {selectedSchool?.schoolName || "--"}
                  </Typography>

                  {selectedSchool?.schoolAddress && (
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: "text.secondary",
                        display: "block",
                      }}
                    >
                      {selectedSchool?.schoolAddress?.address},{" "}
                      {selectedSchool?.schoolAddress?.dist}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{ textAlign: "right" }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  TRANSPORT CARD
                </Typography>

                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "text.secondary",
                    display: "block",
                  }}
                >
                  Academic Session: {selectedSession || "--"}
                </Typography>
              </Box>
            </Stack>
          );
        })()}

        <Divider sx={{ mb: 1 }} />

        {/* Main content: responsive two-column */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          {/* Left - student summary */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "flex-start",
              flex: 1,
            }}
          >
            <Box>
              <Avatar
                variant="square"
                src={
                  student?.studentDocId?.profilePic ||
                  student?.profilePicture ||
                  ""
                }
                sx={{
                  width: 84,
                  height: 84,
                  borderRadius: 1.5,
                  objectFit: "cover",
                }}
              />

              <Typography variant="body1" sx={{ mt: 0.75 }}>
                <strong>Adm No:</strong>{" "}
                {student?.studentDocId?.admissionNumber || "--"}
              </Typography>
            </Box>

            <Box direction="column">
              <Box sx={{ mb: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "primary.main",
                    mb: 0.5,
                  }}
                  variant="h6"
                >
                  {fullName || "--"}
                </Typography>
                <Divider sx={{ mb: 0.5 }} />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  columnGap: 1,
                  rowGap: 0.5,
                  alignItems: "center",
                }}
              >
                <Typography variant="body1">
                  <strong>Father:</strong>
                </Typography>
                <Typography variant="body1">
                  {student?.studentDocId?.fatherName ||
                    student?.studentDocId?.fatherInfo?.firstName ||
                    "--"}
                </Typography>

                <Typography variant="body1">
                  <strong>Class / Section:</strong>
                </Typography>
                <Typography variant="body1">
                  {student?.classInfo?.className || "--"} {"/"} {student?.classInfo?.section}
                </Typography>

                <Typography variant="body1">
                  <strong>Mobile:</strong>
                </Typography>
                <Typography variant="body1">
                  {student?.studentDocId?.mobileNumber || "--"}
                </Typography>

                <Typography variant="body1">
                  <strong>Stop:</strong>
                </Typography>
                <Typography variant="body1">{student?.to || "--"}</Typography>
              </Box>
            </Box>
          </Box>

          {/* Vertical separator for larger screens and print */}
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              mx: 1,
              backgroundColor: "#000",
              width: "1px",
              alignSelf: "stretch",
              height: "auto",
              display: { xs: "none", sm: "block" },
              // ensure dark & thicker in print
              "@media print": {
                backgroundColor: "#000",
                width: "1px",
              },
            }}
          />

          {/* Right - transport details */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ mb: 1 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "primary.main",
                  mb: 0.5,
                }}
                variant="h6"
              >
                Transport Details
              </Typography>
              <Divider sx={{ mb: 0.5 }} />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                columnGap: 1,
                rowGap: 0.5,
                alignItems: "center",
              }}
            >
              <Typography variant="body1">
                <strong>Driver:</strong>
              </Typography>
              <Typography variant="body1">
                {FormatFullName(student?.driverInfo?.firstName, student?.driverInfo?.middleName, student?.driverInfo?.lastName) || "--"} {" / "}{" "}
                {student?.driverInfo?.mobileNumber || "--"}
              </Typography>

              <Typography variant="body1">
                <strong>Conductor:</strong>
              </Typography>
              <Typography variant="body1">
                {FormatFullName(student?.conductorInfo?.firstName, student?.conductorInfo?.middleName, student?.conductorInfo?.lastName) || "--"} {" / "}{" "}
                {student?.conductorInfo?.mobileNumber || "--"}
              </Typography>

              <Typography variant="body1">
                <strong>Route:</strong>
              </Typography>
              <Typography variant="body1">
                {student?.routeInfo?.routeName || "--"} ({student?.routeInfo?.routeNumber || "--"})
              </Typography>

              <Typography variant="body1">
                <strong>Vehicle Number:</strong>
              </Typography>
              <Typography variant="body1">
                {student?.vehicleNumber || "--"} ({student?.routeInfo?.routeNumber || "--"})
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ mb: 1, mt: 1 }} />

        {/* Footer area for small notes */}
        <Box direction="row" justifyContent="space-between" display="flex">
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Note: Carry this card while boarding the transport.
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            School Number: {selectedSchool?.schoolContactNumber || "--"}
          </Typography>
        </Box>
      </CardContainer>
    </React.Fragment>
  );
};

export default TransportCardTemplate1;

const CardContainer = styled(Box)(({ color = "#e6ffe9" }) => ({
  borderRadius: 12,
  backgroundColor: color,
  border: "1.5px solid rgba(0,0,0,0.7)",
  padding: 16,
  marginBottom: 12,
  pageBreakInside: "avoid",
  WebkitPrintColorAdjust: "exact",
  "& .MuiTypography-root": {
    color: "rgba(0,0,0,0.7)",
  },
  "& .MuiDivider-root": {
    backgroundColor: "#000",
    height: 1,
  },
  "& .MuiDivider-vertical": {
    backgroundColor: "#000",
    width: 1,
    alignSelf: "stretch",
    height: "auto",
  },
}));
